import { useCallback, useEffect, useState } from "react";
import { CalendarPlus, Trash2 } from "lucide-react";
import { adminApi, errText } from "./adminApi";

export default function AdminDaily() {
  const [data, setData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [sched, setSched] = useState(null);
  const [dayMap, setDayMap] = useState({});
  const [date, setDate] = useState("");
  const [questionId, setQuestionId] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    const [{ data: d }, { data: q }, { data: sc }] = await Promise.all([
      adminApi.get("/admin/daily"), adminApi.get("/admin/test/questions"), adminApi.get("/admin/daily/schedule")]);
    setData(d);
    setQuestions(q.questions.filter((x) => x.active));
    setSched(sc);
    setDayMap(sc.map || {});
  }, []);
  useEffect(() => { load().catch(() => setError("Could not load daily questions. Click Refresh.")); }, [load]);

  const schedule = async () => {
    setError("");
    setNotice("");
    try {
      await adminApi.post("/admin/daily", { date, question_id: questionId });
      setNotice(`Scheduled for ${date}.`);
      setDate("");
      setQuestionId("");
      await load();
    } catch (err) { setError(errText(err)); }
  };

  const unschedule = async (d) => {
    if (!window.confirm(`Remove the scheduled question for ${d}?`)) return;
    await adminApi.delete(`/admin/daily/${d}`);
    await load();
  };

  const saveDayMap = async () => {
    setError("");
    try {
      await adminApi.put("/admin/daily/schedule", { map: dayMap });
      setNotice("Weekly chapter schedule saved.");
      await load();
    } catch (err) { setError(errText(err)); }
  };

  return (
    <div data-testid="admin-daily">
      <h4 style={{ marginBottom: 4 }}>Daily Question Scheduler</h4>
      <span className="lead-sub">Pick any active question from the bank for a future date. Days without a schedule follow the weekly chapter plan below (AI writes a fresh question from that chapter; if AI is off, a bank question from that chapter is used).</span>
      {error && <p className="admin-error" data-testid="daily-error" style={{ marginTop: 12 }}>{error}</p>}
      {notice && <p className="tp-success-box" style={{ background: "#123a2e", border: "1px solid #12b76a", color: "#4ade80" }}>{notice}</p>}
      {sched && <div className="admin-card" style={{ marginTop: 14 }} data-testid="daily-weekly-schedule">
        <h4 style={{ marginBottom: 6 }}>Weekly chapter schedule</h4>
        <p className="lead-sub">Which chapter should the Daily Question come from on each weekday? Leave a day on &quot;Auto-rotate&quot; to let the system cycle chapters itself.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10, marginTop: 10 }}>
          {sched.weekdays.map((name, i) => (
            <label className="admin-label" key={name}>{name.toUpperCase()}{i === sched.today_weekday && <span className="status-pill approved" style={{ marginLeft: 6 }}>TODAY</span>}
              <select className="admin-input" value={dayMap[String(i)] || ""} onChange={(e) => setDayMap((c) => ({ ...c, [String(i)]: e.target.value }))} data-testid={`daily-day-${i}`}>
                <option value="">Auto-rotate</option>
                {sched.topics.map((t) => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}
              </select>
            </label>
          ))}
        </div>
        <button className="admin-btn" style={{ marginTop: 12 }} onClick={saveDayMap} data-testid="daily-schedule-save">Save weekly schedule</button>
      </div>}
      <div className="admin-card" style={{ marginTop: 14 }}>
        <div className="admin-row" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
          <label className="admin-label">DATE<input type="date" className="admin-input" value={date} min={data?.today} onChange={(e) => setDate(e.target.value)} data-testid="daily-date" /></label>
          <label className="admin-label" style={{ flex: 1, minWidth: 260 }}>QUESTION<select className="admin-input" value={questionId} onChange={(e) => setQuestionId(e.target.value)} data-testid="daily-question"><option value="">Select an active question…</option>{questions.map((q) => <option key={q.question_id} value={q.question_id}>[{q.topic_name}] {q.text.slice(0, 90)}{q.text.length > 90 ? "…" : ""}</option>)}</select></label>
          <button className="admin-btn" disabled={!date || !questionId} onClick={schedule} data-testid="daily-schedule"><CalendarPlus size={14} /> Schedule</button>
        </div>
      </div>
      {data && <div className="table-scroll"><table className="leads-table" data-testid="daily-table">
        <thead><tr><th>DATE</th><th>QUESTION</th><th>ATTEMPTS</th><th></th></tr></thead>
        <tbody>{data.scheduled.map((s, i) => (
          <tr key={s.date} data-testid={`daily-row-${i}`}>
            <td><b>{s.date}</b>{s.date === data.today && <span className="status-pill approved" style={{ marginLeft: 8 }}>TODAY</span>}</td>
            <td style={{ maxWidth: 420 }}><span className="lead-sub" style={{ color: "#dbe6f4" }}>{s.question_text.slice(0, 110)}{s.question_text.length > 110 ? "…" : ""}</span></td>
            <td>{s.attempts}</td>
            <td><button className="admin-btn" onClick={() => unschedule(s.date)} data-testid={`daily-remove-${i}`}><Trash2 size={13} /></button></td>
          </tr>
        ))}</tbody>
      </table></div>}
      {data && data.scheduled.length === 0 && <div className="admin-empty">Nothing scheduled. Until you schedule, each day auto-picks a different question from the active bank.</div>}
    </div>
  );
}
